/**
 * PackageAdminPage — `package/02-admin` read-view (BE-S1 seam, mock-first).
 *
 * A W0 foundationökre épül: **DetailPageTabs** (TabShell #348) + **DataTable** (#334) + a
 * `PackageAdmin` read-DTO (`category` FREE/RENTABLE/CONSIGNMENT 3-csoport). Az item-nevek a
 * `linkedProducts[]`-ból resolve-olva (BE-S1 measured-finding). Ez a READ-nézet; az interaktív
 * szerkesztés (category-toggle, bulk, mennyiség-write) a BE-S2 write-seam-re jön (a proto
 * `02-admin-csomag.html` editor-interakciói).
 */
import { usePackageAdmin } from '@/hooks/use-package-admin';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import { useTranslation } from '@kgc/i18n';
import type { PackageAdmin, PackageAdminItem } from '@kgc/packages/package-admin';
import { DataTable, DetailPageTabs } from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  PACKAGE_ITEM_CATEGORIES,
  groupItemsByCategory,
  resolveItemRef,
} from './package-admin-resolve';

// Q-T042: a settlement-érték-label i18n-síített (a hardcode hu-only volt → t() ×4 nyelv).
// A kulcs-map modul-szintű (ref-stabil), a t() a cell-ben fut (useTranslation a komponensből).
const SETTLEMENT_LABEL_KEY: Record<PackageAdminItem['settlement'], string> = {
  INCLUDED: 'package.item.settlementIncluded',
  RENTAL: 'package.item.settlementRental',
  CONSIGNMENT: 'package.item.settlementConsignment',
};

function formatPrice(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value: Date | null): string {
  return value ? new Date(value).toLocaleDateString('hu-HU') : '—';
}

export function PackageAdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { pkg, isLoading, error } = usePackageAdmin(id);
  const [tab, setTab] = useTabUrlSync('tab', 'items');

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8">
        <p className="text-destructive">{error ?? t('common.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/packages')}
            className="mb-2 text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; {t('package.backToList')}
          </button>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-medium text-primary">{pkg.packageCode}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                pkg.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {pkg.isActive ? t('common.active') : t('common.inactive')}
            </span>
          </div>
          <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">{pkg.name}</h1>
          {pkg.description && (
            <p className="mt-1 text-sm text-muted-foreground">{pkg.description}</p>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <DetailPageTabs
          idPrefix="package-admin"
          ariaLabel={t('package.adminTabs')}
          value={tab}
          onValueChange={setTab}
          tabs={[
            { key: 'items', label: t('package.tabs.items'), content: <ItemsTab pkg={pkg} /> },
            { key: 'data', label: t('package.tabs.data'), content: <DataTab pkg={pkg} /> },
          ]}
        />
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Tételek" tab — 3-csoport accordion (category), minden csoportban egy DataTable
// ─────────────────────────────────────────────────────────────────────────────
function ItemsTab({ pkg }: { pkg: PackageAdmin }) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupItemsByCategory(pkg.items), [pkg.items]);

  const columns: ColumnDef<PackageAdminItem>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('package.item.name'),
        cell: ({ row }) => {
          const ref = resolveItemRef(row.original, pkg.linkedProducts);
          return (
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">{ref.name}</p>
              {ref.sku && <p className="text-xs text-muted-foreground">{ref.sku}</p>}
            </div>
          );
        },
      },
      {
        id: 'quantity',
        header: () => <div className="text-right">{t('common.quantity')}</div>,
        cell: ({ row }) => <div className="text-right tabular-nums">{row.original.quantity}</div>,
      },
      {
        id: 'settlement',
        header: t('package.item.settlement'),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {t(SETTLEMENT_LABEL_KEY[row.original.settlement])}
          </span>
        ),
      },
      {
        id: 'unitPrice',
        header: () => <div className="text-right">{t('package.item.unitPrice')}</div>,
        cell: ({ row }) => (
          <div className="text-right tabular-nums">{formatPrice(row.original.unitPrice)}</div>
        ),
      },
      {
        id: 'webshop',
        header: () => <div className="text-center">Webshop</div>,
        cell: ({ row }) => (
          <div className="text-center">{row.original.visibleOnWebshop ? '✓' : '—'}</div>
        ),
      },
    ],
    [t, pkg.linkedProducts]
  );

  return (
    <div className="space-y-4">
      {PACKAGE_ITEM_CATEGORIES.map(cat => (
        <CategoryAccordion
          key={cat.key}
          categoryKey={cat.key}
          label={t(cat.labelKey)}
          items={groups[cat.key]}
          columns={columns}
        />
      ))}
    </div>
  );
}

function CategoryAccordion({
  categoryKey,
  label,
  items,
  columns,
}: {
  categoryKey: string;
  label: string;
  items: PackageAdminItem[];
  columns: ColumnDef<PackageAdminItem>[];
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        data-testid={`package-category-${categoryKey}`}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-2.5 text-left hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
      >
        <span className="flex items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
          <span
            className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}
            aria-hidden
          >
            ▸
          </span>
          {label}
        </span>
        <span className="rounded-full bg-fg/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
          {items.length}
        </span>
      </button>
      {open && (
        <div className="p-0">
          <DataTable
            columns={columns}
            data={items}
            getRowId={item => item.id}
            emptyMessage="Nincs tétel ebben a csoportban"
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "Csomag-adatok" tab — package-szintű metaadatok
// ─────────────────────────────────────────────────────────────────────────────
function DataTab({ pkg }: { pkg: PackageAdmin }) {
  const { t } = useTranslation();
  const rows: { label: string; value: string }[] = [
    { label: t('common.type'), value: pkg.type },
    { label: t('package.scope'), value: pkg.scope },
    { label: t('package.pricingModel'), value: pkg.pricingModel },
    { label: t('package.fixedPrice'), value: formatPrice(pkg.fixedPrice) },
    { label: t('package.depositAmount'), value: formatPrice(pkg.depositAmount) },
    { label: t('package.validFrom'), value: formatDate(pkg.validFrom) },
    { label: t('package.validTo'), value: formatDate(pkg.validTo) },
  ];
  return (
    <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
      {rows.map(r => (
        <div
          key={r.label}
          className="flex justify-between border-b border-gray-100 py-2 dark:border-gray-800"
        >
          <dt className="text-sm text-muted-foreground">{r.label}</dt>
          <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}
